import {
  AppCard,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiChevronDown } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Typography,
} from "@mui/material";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Term } from "../../../../@types/services/Term";
import type { TermDocument } from "../../../../@types/services/TermDocument";
import type { User } from "../../../../@types/services/User";
import { LoadingCentral } from "../../../../components/molecules/LoadingCentral";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import {
  fetchActiveTermDocument,
  fetchTerms,
} from "../../../../services/terms";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

interface ItemTermProps {
  term: Term;
  user: User;
}

const ItemTerm: React.FC<ItemTermProps> = ({ term, user }) => {
  const { t } = useTranslation();
  const [termDocument, setTermDocument] = useState<TermDocument>();

  const handleFetchTermDocument = useCallback(async (termId: Term["id"]) => {
    fetchActiveTermDocument(termId).then((data) => setTermDocument(data));
  }, []);

  useEffect(() => {
    handleFetchTermDocument(term.id);
  }, [handleFetchTermDocument, term.id]);

  return (
    <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary
        aria-controls="terms-content"
        expandIcon={<Icon icon={mdiChevronDown} />}
      >
        <Typography component="span" sx={{ flexShrink: 0, width: "45%" }}>
          {term.name}
        </Typography>

        <Typography component="span" sx={{ color: "text.secondary" }}>
          {t("terms.lastUpdate")}
          {t("intlDateTime", i18nextParseToDate(term.updated_at))}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        {user?.roles_names.includes("Term - Update") && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              margin: 1,
              padding: 1,
              width: "100%",
            }}
          >
            <ButtonGroup
              aria-label="Small button group"
              size="small"
              sx={{ "& > *": { mr: 1 } }}
            >
              <Button
                color="primary"
                component={Link}
                to={`/vsupplierregistration/terms/${term.id}/edit`}
                variant="contained"
              >
                {t("terms.editTerm")}
              </Button>
            </ButtonGroup>
          </Box>
        )}
        <Divider />

        {!!termDocument && (
          <>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "center",
              }}
            >
              <Typography>{t("terms.form.filePtBr")}</Typography>
              <iframe
                height="800px"
                loading="lazy"
                src={termDocument.attachment_pt_br.url_inline}
                style={{ marginTop: "16px" }}
                title={termDocument.attachment_pt_br.filename}
                width="100%"
              />
            </Box>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "center",
              }}
            >
              <Typography>{t("terms.form.fileEn")}</Typography>
              <iframe
                height="800px"
                loading="lazy"
                src={termDocument.attachment_en.url_inline}
                style={{ marginTop: "16px" }}
                title={termDocument.attachment_en.filename}
                width="100%"
              />
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const RouteComponent: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const terms = Route.useLoaderData();

  const match = Route.useMatch();

  const isLoading = match.isFetching === "loader" || match.status === "pending";

  return (
    <PageContainer maxWidth="xl" showBackButton title={t("terms.title")}>
      <AppCard>
        {user?.roles_names.includes("Term - Update") && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              margin: 1,
              padding: 1,
              width: "100%",
            }}
          >
            <ButtonGroup
              aria-label="Small button group"
              size="small"
              sx={{ "& > *": { mr: 1 } }}
            >
              <Button
                color="primary"
                component={Link}
                to="/vsupplierregistration/terms/new"
                variant="contained"
              >
                {t("terms.newTerm")}
              </Button>
            </ButtonGroup>
          </Box>
        )}
        {!!isLoading && <LoadingCentral />}
        {!isLoading &&
          user &&
          terms.results.map((term) => (
            <ItemTerm key={term.id} term={term} user={user} />
          ))}
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute("/_private/vsupplierregistration/terms/")({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Term - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: RouteComponent,
  loader: () => fetchTerms(),
});
